<script lang="ts">
  import { onMount } from 'svelte';
  import { _ } from 'svelte-i18n';
  import type { PageData } from './$types';
  import ContentCard from '$lib/components/content/ContentCard.svelte';
  
  interface Props {
    data: PageData;
  }
  
  let { data }: Props = $props();
  
  onMount(() => {
    // Track page view
    if (typeof window !== 'undefined' && window.plausible) {
      window.plausible('Press Page View', {
        props: { 
          locale: data.locale,
          articlesCount: data.articles.length
        }
      });
    }
  });
  
  function formatDate(dateString: string, locale: string): string {
    const date = new Date(dateString);
    const localeCode = locale.split('-')[0];
    
    return date.toLocaleDateString(localeCode, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
</script>

<svelte:head>
  <title>{data.meta.title} | Heyweek</title>
  <meta name="description" content={data.meta.description} />
  <link rel="canonical" href="https://heyweek.com{data.meta.canonical}" />
  
  <!-- Open Graph -->
  <meta property="og:title" content="{data.meta.title} | Heyweek" />
  <meta property="og:description" content={data.meta.description} />
  <meta property="og:type" content="website" />
  <meta property="og:url" content="https://heyweek.com{data.meta.canonical}" />
  
  <!-- Twitter Card -->
  <meta name="twitter:title" content="{data.meta.title} | Heyweek" />
  <meta name="twitter:description" content={data.meta.description} />
</svelte:head>

<div class="press-page">
  <!-- Hero Section -->
  <section class="press-hero">
    <div class="container">
      <div class="press-hero__content">
        <h1 class="press-hero__title">{$_('press.title')}</h1>
        <p class="press-hero__subtitle">{$_('press.subtitle')}</p>
        
        <!-- Stats -->
        <div class="press-hero__stats">
          <div class="press-stat">
            <span class="press-stat__number">{data.articles.length}</span>
            <span class="press-stat__label">{$_('press.stats.articles')}</span>
          </div>
          <div class="press-stat">
            <span class="press-stat__number">{data.publications.length}</span>
            <span class="press-stat__label">{$_('press.stats.publications')}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
  
  <!-- Featured Articles -->
  {#if data.featuredArticles.length > 0}
    <section class="featured-articles">
      <div class="container">
        <h2 class="featured-articles__title">{$_('press.featured.title')}</h2>
        <div class="featured-articles__grid">
          {#each data.featuredArticles as article}
            <ContentCard item={article} locale={data.locale} />
          {/each}
        </div>
      </div>
    </section>
  {/if}
  
  <!-- All Articles -->
  <section class="press-content">
    <div class="container">
      <div class="press-content__layout">
        <!-- Sidebar -->
        <aside class="press-sidebar">
          <div class="filter-section">
            <h3 class="filter-section__title">{$_('press.filters.publications')}</h3>
            <div class="filter-section__options">
              {#each data.publications as publication}
                <div class="publication-item">
                  <span class="publication-item__name">{publication}</span>
                  <span class="publication-item__count">
                    {data.articles.filter(a => a.publication === publication).length}
                  </span>
                </div>
              {/each}
            </div>
          </div>
          
          <!-- Press Kit -->
          <div class="press-kit">
            <h3 class="press-kit__title">{$_('press.kit.title')}</h3>
            <p class="press-kit__description">{$_('press.kit.description')}</p>
            <a href="/press-kit" class="press-kit__button">
              {$_('press.kit.download')} →
            </a>
          </div>
        </aside>
        
        <!-- Main Content -->
        <main class="press-main">
          {#if data.articles.length > 0}
            <div class="press-articles">
              <div class="press-articles__header">
                <h2 class="press-articles__title">{$_('press.all.title')}</h2>
                <p class="press-articles__count">
                  {$_('press.showing', { values: { count: data.articles.length } })}
                </p>
              </div>
              
              <div class="press-articles__list">
                {#each data.articles as article}
                  <div class="press-article">
                    <div class="press-article__header">
                      <div class="press-article__meta">
                        <span class="press-article__publication">
                          📰 {article.publication}
                        </span>
                        <time class="press-article__date" datetime={article.date}>
                          {formatDate(article.date, data.locale)}
                        </time>
                      </div>
                    </div>
                    
                    <h3 class="press-article__title">
                      <a 
                        href={article.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        class="press-article__link"
                      >
                        {article.title}
                        <svg class="external-icon" viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z" clip-rule="evenodd" />
                          <path fill-rule="evenodd" d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z" clip-rule="evenodd" />
                        </svg>
                      </a>
                    </h3>
                    
                    <p class="press-article__excerpt">{article.excerpt}</p>
                  </div>
                {/each}
              </div>
            </div>
          {:else}
            <div class="press-empty">
              <div class="press-empty__icon">📰</div>
              <h3 class="press-empty__title">{$_('press.empty.title')}</h3>
              <p class="press-empty__description">{$_('press.empty.description')}</p>
            </div>
          {/if}
        </main>
      </div>
    </div>
  </section>
</div>

<style>
  .press-page {
    min-height: 100vh;
  }
  
  .press-hero {
    background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 100%);
    padding: var(--space-12) 0 var(--space-8);
    border-bottom: 1px solid var(--border-light);
  }
  
  .press-hero__content {
    text-align: center;
    max-width: 800px;
    margin: 0 auto;
  }
  
  .press-hero__title {
    font-size: var(--font-size-3xl);
    font-weight: var(--font-weight-bold);
    color: var(--text-primary);
    margin-bottom: var(--space-4);
    line-height: 1.2;
  }
  
  .press-hero__subtitle {
    font-size: var(--font-size-lg);
    color: var(--text-secondary);
    margin-bottom: var(--space-8);
    line-height: 1.5;
  }
  
  .press-hero__stats {
    display: flex;
    justify-content: center;
    gap: var(--space-8);
  }
  
  .press-stat {
    text-align: center;
  }
  
  .press-stat__number {
    display: block;
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-bold);
    color: var(--brand-primary);
    margin-bottom: var(--space-1);
  }
  
  .press-stat__label {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .featured-articles {
    padding: var(--space-12) 0;
    background: var(--bg-secondary);
  }
  
  .featured-articles__title {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-6);
    text-align: center;
  }
  
  .featured-articles__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: var(--space-6);
  }
  
  .press-content {
    padding: var(--space-8) 0;
  }
  
  .press-content__layout {
    display: grid;
    grid-template-columns: 250px 1fr;
    gap: var(--space-8);
    align-items: start;
  }
  
  .press-sidebar {
    position: sticky;
    top: var(--space-8);
  }
  
  .filter-section {
    background: var(--bg-secondary);
    border-radius: var(--radius-lg);
    padding: var(--space-6);
    margin-bottom: var(--space-6);
  }
  
  .filter-section__title {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-4);
  }
  
  .filter-section__options {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  
  .publication-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--space-2) var(--space-3);
    background: var(--bg-primary);
    border-radius: var(--radius-md);
    border: 1px solid var(--border-light);
  }
  
  .publication-item__name {
    font-size: var(--font-size-sm);
    color: var(--text-primary);
  }
  
  .publication-item__count {
    font-size: var(--font-size-xs);
    color: var(--text-tertiary);
    background: var(--bg-tertiary);
    padding: var(--space-1) var(--space-2);
    border-radius: var(--radius-sm);
  }
  
  .press-kit {
    background: var(--brand-primary-light);
    border-radius: var(--radius-lg);
    padding: var(--space-6);
    text-align: center;
  }
  
  .press-kit__title {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    color: var(--brand-primary-dark);
    margin-bottom: var(--space-3);
  }
  
  .press-kit__description {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    margin-bottom: var(--space-4);
    line-height: 1.4;
  }
  
  .press-kit__button {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    padding: var(--space-2) var(--space-4);
    background: var(--brand-primary);
    color: white;
    text-decoration: none;
    border-radius: var(--radius-md);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    transition: var(--transition-colors);
  }
  
  .press-kit__button:hover {
    background: var(--brand-primary-dark);
  }
  
  .press-articles__header {
    margin-bottom: var(--space-6);
  }
  
  .press-articles__title {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-2);
  }
  
  .press-articles__count {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
  }
  
  .press-articles__list {
    display: flex;
    flex-direction: column;
    gap: var(--space-6);
  }
  
  .press-article {
    background: var(--bg-primary);
    border: 1px solid var(--border-light);
    border-radius: var(--radius-lg);
    padding: var(--space-6);
    transition: var(--transition-all);
  }
  
  .press-article:hover {
    border-color: var(--border-medium);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  .press-article__header {
    margin-bottom: var(--space-4);
  }
  
  .press-article__meta {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    font-size: var(--font-size-sm);
  }
  
  .press-article__publication {
    color: var(--text-secondary);
    font-weight: var(--font-weight-medium);
  }
  
  .press-article__date {
    color: var(--text-tertiary);
  }
  
  .press-article__title {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    line-height: 1.3;
    margin-bottom: var(--space-3);
  }
  
  .press-article__link {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    color: var(--text-primary);
    text-decoration: none;
    transition: var(--transition-colors);
  }
  
  .press-article__link:hover {
    color: var(--brand-primary);
  }
  
  .external-icon {
    width: 16px;
    height: 16px;
    opacity: 0.6;
    flex-shrink: 0;
  }
  
  .press-article__excerpt {
    color: var(--text-secondary);
    line-height: 1.5;
  }
  
  .press-empty {
    text-align: center;
    padding: var(--space-12) var(--space-4);
  }
  
  .press-empty__icon {
    font-size: 4rem;
    margin-bottom: var(--space-4);
    opacity: 0.6;
  }
  
  .press-empty__title {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-3);
  }
  
  .press-empty__description {
    font-size: var(--font-size-base);
    color: var(--text-secondary);
    line-height: 1.5;
  }
  
  @media (max-width: 1024px) {
    .press-content__layout {
      grid-template-columns: 1fr;
      gap: var(--space-6);
    }
    
    .press-sidebar {
      position: static;
      order: -1;
    }
    
    .filter-section {
      padding: var(--space-4);
    }
  }
  
  @media (max-width: 768px) {
    .press-hero__title {
      font-size: var(--font-size-xl);
    }
    
    .press-hero__subtitle {
      font-size: var(--font-size-base);
    }
    
    .press-hero__stats {
      flex-direction: column;
      gap: var(--space-4);
    }
    
    .featured-articles__grid {
      grid-template-columns: 1fr;
    }
    
    .press-article__meta {
      flex-direction: column;
      align-items: flex-start;
      gap: var(--space-1);
    }
  }
</style>
